import React, { useEffect, useMemo, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Panel, Badge } from "@/components/Bits";
import {
  Megaphone, Plus, Trash2, Pencil, Send, History, CheckCircle2, XCircle,
  Clock, Loader2, Radio, Layers, Languages, Users,
} from "lucide-react";
import { toast } from "sonner";

const ipt = "w-full bg-bg border border-line rounded-sm px-3 py-2 text-sm focus:border-cyan focus:outline-none focus:ring-1 focus:ring-cyan/40";

const CATEGORY_META = {
  MAINTENANCE: { color: "info", icon: Layers, label: "Maintenance" },
  GANGGUAN:    { color: "warning", icon: Radio, label: "Gangguan" },
  RECOVERY:    { color: "success", icon: CheckCircle2, label: "Recovery" },
  CUSTOM:      { color: "muted", icon: Megaphone, label: "Custom" },
};

const STATUS_META = {
  SCHEDULED: { color: "awaiting", icon: Clock,       label: "Scheduled" },
  PENDING:   { color: "info",     icon: Loader2,     label: "Pending"   },
  RUNNING:   { color: "info",     icon: Loader2,     label: "Running"   },
  COMPLETED: { color: "success",  icon: CheckCircle2,label: "Completed" },
  FAILED:    { color: "error",    icon: XCircle,     label: "Failed"    },
  CANCELLED: { color: "muted",    icon: XCircle,     label: "Cancelled" },
};

export default function Broadcast() {
  const { user } = useAuth();
  const canEdit = ["admin", "supervisor"].includes(user?.role);
  const [tab, setTab] = useState("send");

  return (
    <div className="p-6 space-y-4 animate-slide-up" data-testid="broadcast-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-cyan mb-1">Broadcast</div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Megaphone className="w-6 h-6 text-cyan" /> Pengumuman ke Telegram
          </h1>
          <p className="text-xs text-slate-400 mt-1">
            Kirim template Maintenance / Gangguan / Recovery ke seluruh grup Telegram terdaftar. Otomatis pakai bahasa default tiap grup.
          </p>
        </div>
        <div className="flex items-center gap-1 border border-line rounded-sm p-0.5">
          <TabBtn active={tab === "send"}      onClick={() => setTab("send")}      testid="tab-send">      <Send className="w-3.5 h-3.5" /> Send</TabBtn>
          <TabBtn active={tab === "templates"} onClick={() => setTab("templates")} testid="tab-templates"> <Layers className="w-3.5 h-3.5" /> Templates</TabBtn>
          <TabBtn active={tab === "history"}   onClick={() => setTab("history")}   testid="tab-history">   <History className="w-3.5 h-3.5" /> History</TabBtn>
        </div>
      </div>

      {tab === "send"      && <SendBroadcast canEdit={canEdit} onSent={() => setTab("history")} />}
      {tab === "templates" && <TemplatesTab  canEdit={canEdit} />}
      {tab === "history"   && <HistoryTab />}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm transition-colors ${
        active ? "bg-cyan/10 text-cyan border border-cyan/40" : "border border-transparent text-slate-400 hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}

// ---------- TEMPLATES TAB ----------
function TemplatesTab({ canEdit }) {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    const { data } = await api.get("/broadcast/templates");
    setList(data);
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-3" data-testid="broadcast-templates-tab">
      {canEdit && (
        <div className="flex justify-end">
          <button
            data-testid="new-broadcast-template-btn"
            onClick={() => { setCreating(true); setEditing({ name: "", category: "CUSTOM", body_id: "", body_en: "", body_zh: "" }); }}
            className="bg-cyan text-bg font-semibold px-4 py-2 rounded-sm text-sm flex items-center gap-2 hover:bg-cyan-dark"
          >
            <Plus className="w-4 h-4" /> New Template
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {list.map((t) => {
          const meta = CATEGORY_META[t.category] || CATEGORY_META.CUSTOM;
          const Icon = meta.icon;
          return (
            <div key={t.id} className="bg-surface border border-line rounded-sm p-3 hover:border-cyan/40 transition-colors" data-testid={`bcast-tpl-${t.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-cyan" />
                  <div>
                    <div className="text-sm font-semibold">{t.name}</div>
                    <Badge color={meta.color} className="mt-1">{meta.label}</Badge>
                  </div>
                </div>
                {canEdit && (
                  <div className="flex gap-1">
                    <button data-testid={`bcast-tpl-edit-${t.id}`} onClick={() => { setEditing(t); setCreating(false); }} className="text-cyan border border-line px-2 py-1 text-[10px] uppercase tracking-wider hover:bg-surface2 rounded-sm flex items-center gap-1">
                      <Pencil className="w-3 h-3" /> Edit
                    </button>
                    <button data-testid={`bcast-tpl-delete-${t.id}`} onClick={async () => { if (confirm(`Delete "${t.name}"?`)) { await api.delete(`/broadcast/templates/${t.id}`); toast.success("Template deleted"); load(); } }} className="text-rose-400 border border-line px-2 py-1 text-[10px] uppercase tracking-wider hover:bg-rose-500/20 rounded-sm">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
              <div className="space-y-1.5">
                {["id", "en", "zh"].map((lng) => {
                  const body = t[`body_${lng}`];
                  if (!body) return null;
                  return (
                    <details key={lng} className="bg-bg border border-line/50 rounded-sm">
                      <summary className="px-2.5 py-1.5 text-[11px] cursor-pointer flex items-center gap-2 hover:bg-surface2">
                        <Languages className="w-3 h-3 text-slate-500" />
                        <span className="font-semibold uppercase">{lng}</span>
                        <span className="text-slate-500 truncate flex-1">{body.split("\n")[0].slice(0, 60)}</span>
                      </summary>
                      <pre className="px-2.5 pb-2 pt-1 text-[11px] whitespace-pre-wrap font-sans text-slate-200 max-h-48 overflow-y-auto">{body}</pre>
                    </details>
                  );
                })}
              </div>
            </div>
          );
        })}
        {list.length === 0 && (
          <div className="col-span-full text-center py-10 text-slate-500 text-sm">Belum ada template. Klik New Template untuk membuat.</div>
        )}
      </div>

      {editing && (
        <TemplateModal tpl={editing} creating={creating} onClose={() => { setEditing(null); setCreating(false); }} onSaved={() => { setEditing(null); setCreating(false); load(); }} />
      )}
    </div>
  );
}

function TemplateModal({ tpl, creating, onClose, onSaved }) {
  const [f, setF] = useState(tpl);
  const [lng, setLng] = useState("id");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      if (creating) await api.post("/broadcast/templates", f);
      else await api.patch(`/broadcast/templates/${f.id}`, {
        name: f.name, category: f.category,
        body_id: f.body_id, body_en: f.body_en, body_zh: f.body_zh,
      });
      toast.success("Template saved");
      onSaved();
    } catch (e2) { setErr(formatErr(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="bcast-tpl-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-surface border border-line rounded-sm p-5 w-full max-w-3xl space-y-3">
        <h3 className="text-lg font-bold">{creating ? "New Broadcast Template" : "Edit Broadcast Template"}</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400">Name</label>
            <input data-testid="bcast-tpl-name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={ipt} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400">Category</label>
            <select data-testid="bcast-tpl-category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className={ipt}>
              <option value="MAINTENANCE">Maintenance</option>
              <option value="GANGGUAN">Gangguan</option>
              <option value="RECOVERY">Recovery</option>
              <option value="CUSTOM">Custom</option>
            </select>
          </div>
        </div>

        <div className="border border-line rounded-sm">
          <div className="flex border-b border-line">
            {["id", "en", "zh"].map((l) => (
              <button
                type="button" key={l}
                data-testid={`bcast-tpl-tab-${l}`}
                onClick={() => setLng(l)}
                className={`flex-1 text-xs uppercase tracking-wider py-2 ${
                  lng === l ? "bg-cyan/10 text-cyan border-b-2 border-cyan" : "text-slate-400 hover:bg-surface2"
                }`}
              >
                {l === "id" ? "Indonesia" : l === "en" ? "English" : "中文"}
              </button>
            ))}
          </div>
          <textarea
            data-testid={`bcast-tpl-body-${lng}`}
            value={f[`body_${lng}`] || ""}
            onChange={(e) => setF({ ...f, [`body_${lng}`]: e.target.value })}
            rows={10}
            placeholder={`Pesan untuk grup berbahasa ${lng.toUpperCase()}…`}
            className="w-full bg-bg p-3 text-sm font-mono focus:outline-none border-0"
          />
        </div>
        <p className="text-[10px] text-slate-500">
          Tip: gunakan placeholder seperti <span className="font-mono text-cyan">[TANGGAL]</span>, <span className="font-mono text-cyan">[JAM]</span>, <span className="font-mono text-cyan">[LAYANAN]</span> dan edit sebelum kirim. Bot kirim sesuai bahasa default tiap grup.
        </p>

        {err && <div className="text-xs text-rose-400">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="border border-line px-4 py-2 text-sm rounded-sm hover:bg-surface2">Cancel</button>
          <button data-testid="bcast-tpl-save" disabled={busy} className="bg-cyan text-bg font-semibold px-4 py-2 text-sm rounded-sm hover:bg-cyan-dark disabled:opacity-60">Save</button>
        </div>
      </form>
    </div>
  );
}

// ---------- SEND TAB ----------
function SendBroadcast({ canEdit, onSent }) {
  const [templates, setTemplates] = useState([]);
  const [chats, setChats] = useState([]);
  const [tplId, setTplId] = useState("");
  const [mode, setMode] = useState("ALL");
  const [system, setSystem] = useState("TARAPAY");
  const [picked, setPicked] = useState([]);
  const [override, setOverride] = useState("");
  const [scheduled, setScheduled] = useState(false);
  const [schedDt, setSchedDt] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);

  useEffect(() => {
    api.get("/broadcast/templates").then((r) => setTemplates(r.data));
    api.get("/telegram/chats").then((r) => setChats(r.data));
  }, []);

  const tpl = templates.find((t) => t.id === tplId);
  const meta = tpl ? (CATEGORY_META[tpl.category] || CATEGORY_META.CUSTOM) : null;

  const targets = useMemo(() => {
    if (mode === "ALL") return chats;
    if (mode === "SYSTEM") return chats.filter((c) => c.system === system);
    if (mode === "PICK") return chats.filter((c) => picked.includes(c.id));
    return [];
  }, [mode, system, picked, chats]);

  const togglePick = (mid) => {
    setPicked((p) => p.includes(mid) ? p.filter((x) => x !== mid) : [...p, mid]);
  };

  const send = async () => {
    if (!tplId) { toast.error("Pilih template dulu"); return; }
    if (targets.length === 0) { toast.error("Tidak ada target group"); return; }
    setBusy(true);
    try {
      const body = {
        template_id: tplId,
        target_mode: mode,
        system: mode === "SYSTEM" ? system : null,
        mapping_ids: mode === "PICK" ? picked : [],
        override_language: override || null,
        scheduled_at: scheduled && schedDt ? new Date(schedDt).toISOString() : null,
      };
      const { data } = await api.post("/broadcast/send", body);
      toast.success(scheduled ? `Scheduled to ${targets.length} target(s)` : `Sending to ${targets.length} target(s)`);
      setConfirm(false);
      onSent?.(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="broadcast-send-tab">
      {/* Composer */}
      <Panel title="Compose Broadcast" testid="broadcast-composer">
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400">Template</label>
            <select data-testid="bcast-select-template" value={tplId} onChange={(e) => setTplId(e.target.value)} className={ipt}>
              <option value="">— Pilih template —</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>[{t.category}] {t.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
              <Users className="w-3 h-3" /> Target Groups
            </label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {["ALL", "SYSTEM", "PICK"].map((m) => (
                <button
                  key={m} type="button"
                  data-testid={`bcast-mode-${m.toLowerCase()}`}
                  onClick={() => setMode(m)}
                  className={`text-xs uppercase tracking-wider py-2 rounded-sm border ${
                    mode === m ? "bg-cyan/10 border-cyan/40 text-cyan" : "border-line text-slate-400 hover:bg-surface2"
                  }`}
                >
                  {m === "ALL" ? "Semua" : m === "SYSTEM" ? "Per System" : "Pilih Manual"}
                </button>
              ))}
            </div>
            {mode === "SYSTEM" && (
              <select data-testid="bcast-system-filter" value={system} onChange={(e) => setSystem(e.target.value)} className={`${ipt} mt-2`}>
                <option value="TARAPAY">TARAPAY</option>
                <option value="SATURNUS">SATURNUS</option>
              </select>
            )}
            {mode === "PICK" && (
              <div className="mt-2 border border-line rounded-sm max-h-44 overflow-y-auto" data-testid="bcast-chat-picker">
                {chats.length === 0 && <div className="text-center text-xs text-slate-500 py-3">Belum ada grup terdaftar</div>}
                {chats.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface2 cursor-pointer border-b border-line/50 last:border-0">
                    <input
                      type="checkbox"
                      data-testid={`bcast-pick-${c.id}`}
                      checked={picked.includes(c.id)}
                      onChange={() => togglePick(c.id)}
                    />
                    <span className="text-xs flex-1 truncate">
                      {c.chat_title || c.merchant_name}
                      {c.thread_id ? <span className="text-cyan ml-1">· {c.thread_name || `Topic ${c.thread_id}`}</span> : null}
                    </span>
                    <Badge color="muted" className="text-[9px]">{c.system}</Badge>
                    <Badge color="info" className="text-[9px]">{c.default_language}</Badge>
                  </label>
                ))}
              </div>
            )}
            <div className="text-[10px] text-slate-500 mt-1">
              {targets.length} grup akan menerima.
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400">Override Language</label>
            <select data-testid="bcast-override-lang" value={override} onChange={(e) => setOverride(e.target.value)} className={ipt}>
              <option value="">Auto (per grup default)</option>
              <option value="ID">Force Indonesia</option>
              <option value="EN">Force English</option>
              <option value="ZH">Force 中文</option>
            </select>
          </div>

          <div>
            <label className="flex items-center gap-2 text-[11px] text-slate-300">
              <input type="checkbox" data-testid="bcast-schedule-toggle" checked={scheduled} onChange={(e) => setScheduled(e.target.checked)} />
              <Clock className="w-3.5 h-3.5 text-slate-400" /> Jadwalkan kirim
            </label>
            {scheduled && (
              <input
                type="datetime-local"
                data-testid="bcast-schedule-dt"
                value={schedDt}
                onChange={(e) => setSchedDt(e.target.value)}
                className={`${ipt} mt-2`}
                min={new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)}
              />
            )}
          </div>

          <button
            type="button"
            data-testid="bcast-send-btn"
            disabled={!canEdit || !tplId || targets.length === 0 || (scheduled && !schedDt)}
            onClick={() => setConfirm(true)}
            className="w-full bg-cyan text-bg font-semibold py-2.5 rounded-sm flex items-center justify-center gap-2 hover:bg-cyan-dark disabled:opacity-50"
          >
            <Send className="w-4 h-4" /> {scheduled ? "Jadwalkan" : "Kirim Sekarang"}
          </button>
          {!canEdit && <div className="text-[10px] text-amber-400 text-center">Hanya admin/supervisor yang dapat mengirim broadcast.</div>}
        </div>
      </Panel>

      {/* Preview */}
      <Panel title="Preview" testid="broadcast-preview">
        {!tpl ? (
          <div className="text-center text-slate-500 text-sm py-10">Pilih template untuk preview pesan.</div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              {meta && <meta.icon className="w-4 h-4 text-cyan" />}
              <span className="font-semibold text-sm">{tpl.name}</span>
              {meta && <Badge color={meta.color}>{meta.label}</Badge>}
            </div>
            {["id", "en", "zh"].map((lng) => {
              const used = targets.some((t) => (override || t.default_language || "ID").toUpperCase() === lng.toUpperCase());
              const body = tpl[`body_${lng}`];
              if (!body) return null;
              return (
                <div key={lng} className={`bg-bg border rounded-sm p-2.5 ${used ? "border-cyan/40" : "border-line/50 opacity-60"}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[10px] uppercase font-semibold tracking-wider text-slate-400">{lng}</span>
                    {used && <Badge color="success" className="text-[9px]">Akan dikirim</Badge>}
                  </div>
                  <pre className="text-[11px] whitespace-pre-wrap font-sans text-slate-200">{body}</pre>
                </div>
              );
            })}
            <div className="border-t border-line pt-2 text-[10px] text-slate-500">
              {targets.length} target group{targets.length !== 1 ? "s" : ""}
              {override && <> · Forced language: <span className="text-cyan">{override}</span></>}
            </div>
          </div>
        )}
      </Panel>

      {confirm && (
        <ConfirmDialog
          tpl={tpl}
          targets={targets}
          scheduled={scheduled && !!schedDt}
          schedDt={schedDt}
          onCancel={() => setConfirm(false)}
          onConfirm={send}
          busy={busy}
        />
      )}
    </div>
  );
}

function ConfirmDialog({ tpl, targets, scheduled, schedDt, onCancel, onConfirm, busy }) {
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onCancel} data-testid="bcast-confirm-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-surface border border-line rounded-sm p-5 w-full max-w-md space-y-3">
        <h3 className="text-lg font-bold flex items-center gap-2"><Megaphone className="w-5 h-5 text-cyan" /> Konfirmasi Broadcast</h3>
        <div className="text-sm text-slate-300">
          <p>Anda akan mengirim <span className="text-cyan font-semibold">{tpl?.name}</span> ke <span className="text-cyan font-semibold">{targets.length}</span> grup Telegram.</p>
          {scheduled && (
            <p className="mt-2 text-amber-400 text-xs flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> Dijadwalkan: {new Date(schedDt).toLocaleString("id-ID")}
            </p>
          )}
        </div>
        <div className="bg-bg border border-line rounded-sm p-2 max-h-32 overflow-y-auto">
          {targets.map((t) => (
            <div key={t.id} className="text-[11px] text-slate-400 flex items-center justify-between py-0.5">
              <span className="truncate">
                {t.chat_title || t.merchant_name}
                {t.thread_id ? <span className="text-cyan ml-1">· {t.thread_name || `Topic ${t.thread_id}`}</span> : null}
              </span>
              <Badge color="muted" className="text-[9px]">{t.default_language || "ID"}</Badge>
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onCancel} className="border border-line px-4 py-2 text-sm rounded-sm hover:bg-surface2">Batal</button>
          <button data-testid="bcast-confirm-send" onClick={onConfirm} disabled={busy} className="bg-cyan text-bg font-semibold px-4 py-2 text-sm rounded-sm hover:bg-cyan-dark disabled:opacity-60 flex items-center gap-2">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            {scheduled ? "Jadwalkan" : "Kirim Sekarang"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- HISTORY TAB ----------
function HistoryTab() {
  const [jobs, setJobs] = useState([]);
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    const { data } = await api.get("/broadcast/jobs");
    setJobs(data);
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 7000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="space-y-3" data-testid="broadcast-history-tab">
      {jobs.length === 0 && <div className="text-center py-10 text-slate-500 text-sm">Belum ada riwayat broadcast.</div>}
      {jobs.map((j) => {
        const sm = STATUS_META[j.status] || STATUS_META.PENDING;
        const Icon = sm.icon;
        const cat = CATEGORY_META[j.category] || CATEGORY_META.CUSTOM;
        const isOpen = openId === j.id;
        return (
          <div key={j.id} className="bg-surface border border-line rounded-sm" data-testid={`bcast-job-${j.id}`}>
            <button
              onClick={() => setOpenId(isOpen ? null : j.id)}
              className="w-full flex items-center justify-between p-3 hover:bg-surface2 text-left"
            >
              <div className="flex items-center gap-3">
                <Icon className={`w-4 h-4 ${j.status === "RUNNING" || j.status === "PENDING" ? "animate-spin text-cyan" : "text-slate-400"}`} />
                <div>
                  <div className="text-sm font-semibold">{j.template_name}</div>
                  <div className="text-[10px] text-slate-500 flex items-center gap-2 mt-0.5">
                    <Badge color={cat.color} className="text-[9px]">{cat.label}</Badge>
                    <span>{new Date(j.created_at).toLocaleString("id-ID")}</span>
                    {j.created_by_name && <span>· by {j.created_by_name}</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span className="text-emerald-400">{j.sent_count}<span className="text-slate-500">/{j.total_targets} sent</span></span>
                {j.failed_count > 0 && <span className="text-rose-400">{j.failed_count} failed</span>}
                <Badge color={sm.color}>{sm.label}</Badge>
              </div>
            </button>
            {isOpen && (
              <div className="border-t border-line p-3 space-y-2">
                {j.scheduled_at && (
                  <div className="text-[11px] text-amber-400">Scheduled at: {new Date(j.scheduled_at).toLocaleString("id-ID")}</div>
                )}
                {(j.results || []).length === 0 && j.status === "SCHEDULED" && (
                  <div className="text-xs text-slate-500">Menunggu jadwal kirim…</div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                  {(j.results || []).map((r, i) => (
                    <div key={i} className="text-[11px] flex items-center gap-2 bg-bg border border-line/50 rounded-sm px-2 py-1">
                      {r.status === "SENT" ? <CheckCircle2 className="w-3 h-3 text-emerald-400" /> : <XCircle className="w-3 h-3 text-rose-400" />}
                      <span className="truncate flex-1">
                        {r.chat_title || r.chat_id}
                        {r.thread_id ? <span className="text-cyan ml-1">· {r.thread_name || `Topic ${r.thread_id}`}</span> : null}
                      </span>
                      <Badge color="muted" className="text-[9px]">{r.language}</Badge>
                      {r.error && <span className="text-rose-400 truncate max-w-[160px]" title={r.error}>{r.error}</span>}
                    </div>
                  ))}
                </div>
                {j.status === "SCHEDULED" && (
                  <button
                    data-testid={`bcast-cancel-${j.id}`}
                    onClick={async () => { if (confirm("Cancel scheduled broadcast?")) { await api.post(`/broadcast/jobs/${j.id}/cancel`); toast.success("Cancelled"); load(); } }}
                    className="text-rose-400 border border-rose-500/30 hover:bg-rose-500/10 px-3 py-1 text-[10px] uppercase tracking-wider rounded-sm"
                  >
                    Cancel Scheduled
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
